import type {GwgAccelerator} from '@udemy/career-academies';

import {LearningPathStepType} from '../learning-path-status';

import type {CourseCertificateInfo} from 'src/features/google-ai-path-page/hooks/use-course-certificate-url-by-id';

export type LearningPathOutcome = {
    number: number;
    title: string;
    description: string;
};

export type LearningPathCourseStep = {
    id: string | number;
    type: LearningPathStepType.Course;
    courseTitle?: string;
    durationMinutes?: number;
    tags?: string[];
    ratingAverage?: number;
    ratingCount?: number;
    progressPercent?: number;
    thumbnailUrl?: string;
    cta?: {href?: string; certificateHref?: string; certificateReady: boolean};
    details?: {
        title?: string;
        keyPoints?: string[];
    };
};

type BuyableUrls = {urlCourseTaking?: string; urlCourseLanding?: string};
type CourseTakingById = Record<string, string | undefined>;

export function gwgAcceleratorToLearningPath(
    accelerator: GwgAccelerator,
    isEnrolled = false,
    courseTakingById?: CourseTakingById,
    courseProgressById: Record<string, number | null | undefined> = {},
    courseCertificateById: Record<string, CourseCertificateInfo> = {},
) {
    const steps: LearningPathCourseStep[] = (accelerator.curriculum?.sections ?? []).map(
        (section) => {
            const buyable = section.buyable;
            const buyableUrls = buyable as BuyableUrls | undefined;
            const courseTakingUrl =
                buyable?.id && courseTakingById
                    ? courseTakingById[buyable.id.toString()]
                    : undefined;
            const courseHref = isEnrolled
                ? courseTakingUrl ?? buyableUrls?.urlCourseTaking ?? buyableUrls?.urlCourseLanding
                : buyableUrls?.urlCourseLanding;
            const subject = section.subject;
            const durationMinutes =
                typeof buyable?.durationInSeconds === 'number'
                    ? buyable.durationInSeconds / 60
                    : undefined;

            const courseId = buyable?.id ? String(buyable.id) : undefined;
            const progressPercent =
                courseId && typeof courseProgressById[courseId] === 'number'
                    ? courseProgressById[courseId]
                    : undefined;
            const certificate = courseId ? courseCertificateById[courseId] : undefined;
            const certificateHref = certificate?.url;
            const certificateReady = certificate?.isReady || false;

            return {
                id: buyable?.id ?? `${accelerator.numericId}-${buyable?.title ?? 'course'}`,
                type: LearningPathStepType.Course,
                courseTitle: buyable?.title,
                thumbnailUrl: buyable?.images?.px750x422 ?? buyable?.images?.height200,
                durationMinutes,
                // We can enrich these later; keep minimal for now.
                tags: [],
                ratingAverage: buyable?.rating?.average,
                ratingCount: buyable?.rating?.count,
                progressPercent,
                cta: {href: courseHref, certificateHref, certificateReady},
                details: {
                    title: subject?.title,
                    keyPoints: subject?.keyPoints ?? [],
                },
            };
        },
    );

    const outcomes: LearningPathOutcome[] = (accelerator.steps ?? []).map((step) => ({
        number: step.number,
        title: step.title,
        description: step.description,
    }));

    return {steps, outcomes};
}
